import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type JSX,
  type MouseEvent
} from 'react'

import { MarkdownView } from './components/MarkdownView.tsx'
import { DocsNavigateContext } from './components/navigate.ts'
import { resolveSlug } from './lib/pages.ts'
import { pages } from 'virtual:terminal-pages'

const slugs = pages.map(page => page.slug)

function samePageClick(event: MouseEvent<HTMLAnchorElement>): boolean {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  )
}

export function App(): JSX.Element {
  const [path, setPath] = useState(() => window.location.pathname)
  const [navOpen, setNavOpen] = useState(false)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const missingRef = useRef<HTMLParagraphElement>(null)
  const pathRef = useRef(path)
  const fromClick = useRef(false)
  const slug = resolveSlug(path, slugs)
  const page = pages.find(item => item.slug === slug)

  useEffect(() => {
    pathRef.current = path
  }, [path])

  useEffect(() => {
    const onPop = () => setPath(window.location.pathname)
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  useEffect(() => {
    const current = pages.find(item => item.slug === resolveSlug(path, slugs))
    document.title = current
      ? `${current.title} — BlytzPay`
      : 'Page not found — BlytzPay'
    if (!fromClick.current) return
    fromClick.current = false
    window.scrollTo(0, 0)
    const target = current ? headingRef.current : missingRef.current
    target?.focus()
  }, [path])

  const navigate = useCallback(
    (event: MouseEvent<HTMLAnchorElement>, href: string) => {
      if (!samePageClick(event)) return
      event.preventDefault()
      fromClick.current = href !== pathRef.current
      if (window.location.pathname !== href) {
        window.history.pushState({}, '', href)
      }
      setPath(window.location.pathname)
      setNavOpen(false)
    },
    []
  )

  return (
    <DocsNavigateContext.Provider value={navigate}>
      <div className="docs-root" data-testid="app-root">
        <header className="docs-header" data-testid="site-header">
          <a
            className="docs-name"
            href="/terminal/overview"
            data-testid="site-name"
            onClick={event => navigate(event, '/terminal/overview')}
          >
            BlytzPay
          </a>
          <button
            type="button"
            className="docs-menu"
            data-testid="nav-toggle"
            aria-controls="site-nav"
            aria-expanded={navOpen}
            onClick={() => setNavOpen(open => !open)}
          >
            Menu
          </button>
        </header>
        <div className="docs-layout">
          <nav
            id="site-nav"
            className={navOpen ? 'docs-nav docs-nav-open' : 'docs-nav'}
            aria-label="Terminal pages"
            data-testid="site-nav"
            data-open={navOpen ? 'true' : 'false'}
          >
            {pages.map(item => (
              <a
                key={item.slug}
                href={item.path}
                data-testid={`nav-${item.slug}`}
                aria-current={item.slug === slug ? 'page' : undefined}
                onClick={event => navigate(event, item.path)}
              >
                {item.title}
              </a>
            ))}
          </nav>
          <main className="docs-main" data-testid="page-content">
            {page ? (
              <>
                <h1
                  ref={headingRef}
                  className="docs-title"
                  tabIndex={-1}
                  data-testid="page-title"
                >
                  {page.title}
                </h1>
                <MarkdownView key={page.slug} tokens={page.tokens} />
              </>
            ) : (
              <p
                ref={missingRef}
                className="docs-missing"
                tabIndex={-1}
                data-testid="page-not-found"
              >
                This page does not exist.
              </p>
            )}
          </main>
        </div>
      </div>
    </DocsNavigateContext.Provider>
  )
}
